import QuestionImage from './QuestionImage.jsx';
import React from 'react';
import Modal from './Modal.jsx';
import CategoryIcon from './CategoryIcon.jsx';
import ContextBody from './ContextBody.jsx';

// The web Dig Deeper modal (parity W2, Tom 2026-07-30: cards get "yes, no,
// and context buttons that launch pop up modals"). Mirrors iOS's
// DigDeeperSheet: category header in the tint, question text, 16:9 art,
// blurb, source credit on its own line — and the answer buttons live IN the
// modal before submit, so reading the context flows straight into making the
// call. Post-submit (no `onAnswer`), the buttons are absent and the user's
// locked pick shows instead — the sheet is read-only, exactly like iOS.
//
// Bottom-sheet chrome borrowed from OpenQuestionSheet so the app's sheets
// stay one family.
export default function ContextModal({ question: q, onAnswer, onClose }) {
  if (!q) return null;
  const picked = typeof q.answer === 'boolean' ? (q.answer ? 'YES' : 'NO') : null;

  return (
    <Modal onClose={onClose} backdropStyle={backdrop} sheetStyle={sheet} ariaLabel="Question context">
      <button onClick={onClose} aria-label="Close" style={closeBtn}>
        ✕
      </button>

      <div style={catHeader}>
        <CategoryIcon category={q.category} size={20} />
        <span style={{ color: 'var(--gold)', fontWeight: 800, fontSize: 16 }}>{q.category}</span>
      </div>

      <p style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.35, margin: '0 0 14px' }}>{q.text}</p>

      {/* Art returns below the question text (Tom 2026-08-13, second ruling):
          every daily six question is illustrated now, so the slot is a
          consistent part of the brief rather than a lead-only surprise. */}
      <QuestionImage src={q.imageURL} alt={q.category ? `${q.category} question illustration` : ''} />

      <ContextBody question={q} />

      {onAnswer ? (
        // Pre-submit: answering from the modal answers the card and closes —
        // the modal's job is done the moment the call is made.
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <AnswerBtn
            label="YES"
            selected={q.answer === true}
            onClick={() => {
              onAnswer(true);
              onClose();
            }}
          />
          <AnswerBtn
            label="NO"
            selected={q.answer === false}
            onClick={() => {
              onAnswer(false);
              onClose();
            }}
          />
        </div>
      ) : (
        <div style={youRow}>
          <span style={cellLabel}>You:</span>
          <span
            style={{
              fontWeight: 900,
              fontSize: 18,
              color: picked ? 'var(--gold)' : 'var(--secondary-text)',
            }}
          >
            {/* Em-dash for a skip — the same badge everywhere (never "SKIP"). */}
            {picked ?? '—'}
          </span>
        </div>
      )}
    </Modal>
  );
}

function AnswerBtn({ label, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        flex: 1,
        padding: '14px 0',
        borderRadius: 'var(--radius-sm)',
        border: `1.5px solid ${selected ? 'var(--gold)' : 'var(--border)'}`,
        background: selected ? 'var(--gold)' : 'var(--surface)',
        color: selected ? '#1a1a1a' : 'var(--primary-text)',
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '0.04em',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

const backdrop = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 50,
  padding: 0,
};
const sheet = {
  position: 'relative',
  width: '100%',
  maxWidth: 'var(--maxw)',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: '20px 20px 0 0',
  padding: '24px 18px calc(24px + env(safe-area-inset-bottom))',
  outline: 'none',
};
const closeBtn = {
  position: 'absolute',
  top: 16,
  right: 16,
  width: 34,
  height: 34,
  borderRadius: 17,
  border: 'none',
  background: 'var(--surface)',
  color: 'var(--primary-text)',
  fontSize: 16,
  cursor: 'pointer',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
};
const catHeader = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  margin: '4px 0 14px',
  color: 'var(--gold)',
};
const youRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  marginTop: 18,
  background: 'var(--surface)',
  borderRadius: 'var(--radius-sm)',
  padding: '12px 0',
};
const cellLabel = {
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--secondary-text)',
};
